import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, Pencil } from "lucide-react";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { InvoiceTag, LineItem, Polygon } from "@/apis/gql/types";
import { DraftPaymentStatus } from "@/apis/gql/types";
import {
  InvoiceSmbReviewStatus,
  InvoiceStatus,
} from "@/lib/constants/invoiceStatus";
import { useRemoveDuplicates } from "@/queries/invoices/remove-duplicates";
import { useUpdateInvoice } from "@/queries/invoices/update";

import type { AlertVariant } from "@/components/shared/alerts/simple-alert";
import {
  ValidationContext,
  ValidationRoot,
} from "@/components/shared/form/shared/form-validation";
import { InvoiceComments } from "@/components/shared/invoice-comments";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TypographyLarge } from "@/components/ui/typography";
import { usePreviousRoute } from "@/lib/miscellaneous/RouteTracker";
import { useInvoiceBrowser } from "@/providers/invoice-browser";
import { useTenant } from "@/providers/session";
import {
  type DuplicateInvoice,
  type GetInvoiceDetailsInvoice,
  INVOICE_QUERY,
  type InvoiceDetailsDraftPayment,
  type InvoiceDetailsPayment,
  type InvoiceDetailsSupplier,
  type InvoiceDetailsTransaction,
} from "@/queries/invoices/get";
import { useCreateDraftPayment } from "@/queries/payments/draft-payments/create-draft-payment";

import { BU_JUSTIFICATIONS } from "@/components/shared/form/lineItems/taxCode";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { InvoiceAlert } from "../accounting/components/invoice-alert";
import { AiProcessingCard } from "../components/ai-processing-card";
import { NavigationBar } from "../components/navigation-bar";
import { invoiceValidationClientFormSchema } from "../validations/invoice";
import { paymentWarningsSchema } from "../validations/payment";
import InvoiceBankReconciliation from "./components/invoice-bank-reconciliation";
import { InvoiceBanner } from "./components/invoice-banner";
import { InvoiceCostCenterForm } from "./components/invoice-cost-center-form";
import { InvoiceForm } from "./components/invoice-form";
import { PaymentForm } from "./components/payment-form";
import { PaymentsList } from "./components/payment-form/payments-list";

type Props = {
  invoice: GetInvoiceDetailsInvoice;
  lineItems: LineItem[];
  tags: InvoiceTag[];
  duplicates: DuplicateInvoice[];
  payments: InvoiceDetailsPayment[];
  draftPayments: InvoiceDetailsDraftPayment[];
  supplier: InvoiceDetailsSupplier;
  polygons: Polygon;
  transactions: InvoiceDetailsTransaction[];
  updateBox: (polygons?: any, fieldName?: string) => void;
  onDelete: () => void;
  disabled?: boolean;
};

export const InvoiceReviewClientForm = ({
  invoice,
  transactions,
  tags,
  duplicates,
  payments,
  draftPayments,
  supplier,
  polygons,
  lineItems,
  updateBox,
  onDelete,
  disabled = false,
}: Props) => {
  const { t } = useTranslation();
  const [removeDuplicates] = useRemoveDuplicates();
  const [updateInvoice] = useUpdateInvoice();
  const [createDraftPayment] = useCreateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });

  const navigate = useNavigate();
  const { companySlug } = useTenant();
  const { invoices: queue, remove: removeInvoiceFromQueue } =
    useInvoiceBrowser();

  const handleRemoveDuplicates = useCallback(async () => {
    if (!invoice?.invoiceId) return;
    await removeDuplicates({ invoiceIds: [invoice?.invoiceId] });
  }, [invoice?.invoiceId, removeDuplicates]);

  const handleConfirm = useCallback(async () => {
    if (!invoice?.invoiceId) return;
    const toastId = toast.loading(t("generic_loading"));
    try {
      await updateInvoice({
        invoiceId: invoice?.invoiceId,
        smbReviewStatus: InvoiceSmbReviewStatus.REVIEWED,
      });
      toast.dismiss(toastId);
    } catch (error) {
      Sentry.captureException(error);
      toast.error(t("generic_error"), { richColors: true, id: toastId });
      throw error;
    }
  }, [invoice?.invoiceId, updateInvoice]);

  const paymentsToReview = useMemo(
    () =>
      draftPayments.filter(
        (draftPayment) =>
          draftPayment.status &&
          ![DraftPaymentStatus.DRAFT].includes(draftPayment.status),
      ),
    [draftPayments],
  );

  const handleFieldFocus = useCallback(
    (field: any) => {
      updateBox(polygons, field);
    },
    [polygons, updateBox],
  );

  const handleFieldBlur = useCallback(() => {
    updateBox();
  }, [updateBox]);

  useClearBlueBoxOnInvoiceChange();

  const handleDelete = useCallback(async () => {
    onDelete();

    const notInQueue = queue.length === 0;
    if (notInQueue) {
      if (window.history.length > 1) {
        window.history.back();
      } else {
        navigate({
          to: "/$company/unternehmen",
          params: { company: companySlug },
          replace: true,
        });
      }
    } else {
      removeInvoiceFromQueue();
    }
  }, [onDelete, queue.length, navigate, removeInvoiceFromQueue, companySlug]);

  const previousRoute = usePreviousRoute();

  const handlePay = useCallback(async () => {
    await handleConfirm();

    const lastPartOfRoute = previousRoute?.split("/").pop();

    navigate({
      to: "/$company/unternehmen/bezahlen",
      params: { company: companySlug },
      search: {
        invoiceIds: [invoice.invoiceId],
        navigateBackTo: lastPartOfRoute ?? "",
      },
    });
  }, [handleConfirm, invoice.invoiceId, navigate, companySlug, previousRoute]);

  const handlePaidChange = () => {
    if (draftPayments.length === 0) {
      createDraftPayment(invoice.invoiceId, {
        rawAmount: invoice.totalAmount ?? 0,
        currency: invoice.currency,
      });
    }
  };

  const isProcessing = invoice?.status === InvoiceStatus.NEW;

  const missingVatId = lineItems?.some(
    (li) => li.buJustification === BU_JUSTIFICATIONS.B2C,
  );
  const alerts: {
    tagType: "multipleInvoice" | "missingVatId";
    tagDisplayVariant: AlertVariant;
  }[] = missingVatId
    ? [{ tagType: "missingVatId", tagDisplayVariant: "destructive" }]
    : [];
  const preparedTags = (tags ?? [])
    .filter((tag) => tag?.tagName === "Adressat Prüfen")
    .map((tag) => {
      // TODO: these translation are hardcoded for this tag
      const tagName = tag.tagName as "Adressat Prüfen";
      const base = `invoiceReview.tags.client.${tagName}` as const;
      const details = tag.details?.details;
      const mappedDetails = details
        ? {
            [t(
              `${base}.details.companyRecipientNameAnswer` as const,
            ) as string]: {
              flag: details.companyRecipientNameAnswer !== true,
              reasoning: details.companyRecipientNameReasoning,
            },
            [t(
              `${base}.details.companyRecipientAddressAnswer` as const,
            ) as string]: {
              flag: details.companyRecipientAddressAnswer !== true,
              reasoning: details.companyRecipientAddressReasoning,
            },
            [t(
              `${base}.details.companyRecipientVatIdAnswer` as const,
            ) as string]: {
              flag: details.companyRecipientVatIdAnswer !== true,
              reasoning: details.companyRecipientVatIdReasoning,
            },
          }
        : {};
      return {
        tagId: tag.tagId,
        tagName: tag.tagName,
        isApproved: tag.approval === true,
        title: t(`${base}.title`),
        message: t(`${base}.message`),
        subMessage:
          tag.tagName === "Adressat Prüfen"
            ? t(`${base}.submessage`)
            : undefined,
        details: mappedDetails,
      };
    });

  const disabledMessage = disabled ? (
    <Alert className="flex flex-col border-none p-0">
      <AlertTitle>
        {t("invoice_review.client.invoice_data_disabled")}
      </AlertTitle>
      <AlertDescription>
        {t("invoice_review.client.invoice_data_disabled_description")}
      </AlertDescription>
    </Alert>
  ) : undefined;

  return (
    <ValidationRoot
      schema={invoiceValidationClientFormSchema}
      values={{
        ...invoice,
        payments: payments,
      }}
    >
      <div className="flex flex-col h-full">
        <div className="flex-1 flex flex-col gap-4 p-4 h-full overflow-auto">
          <InvoiceBanner
            invoiceId={invoice.invoiceId}
            createdAt={new Date(invoice.createdAt)}
            source={invoice.invoiceSource ?? ""}
            dueDate={new Date(invoice.dueDate)}
            invoiceNo={invoice.invoiceNo}
            onDelete={handleDelete}
            disabled={disabledMessage}
          />
          {isProcessing ? (
            <AiProcessingCard invoiceId={invoice.invoiceId} />
          ) : (
            <>
              <InvoiceAlert
                tags={preparedTags}
                alerts={alerts}
                isDuplicate={invoice.isDuplicate}
                duplicates={duplicates}
                invoiceId={invoice.invoiceId}
                translationKey={"invoiceReview.tags.client"}
              />
              {invoice.currency !== "EUR" && !invoice.isPaid && (
                <CurrencyWarningBanner />
              )}
              <InvoiceForm
                values={invoice}
                onFieldFocus={handleFieldFocus}
                onFieldBlur={handleFieldBlur}
                onPaidChange={handlePaidChange}
                disabled={disabledMessage}
              />
              {paymentsToReview.length > 0 && (
                <PaymentsList payments={paymentsToReview} />
              )}
              {!invoice.isPaid && (
                <ValidationRoot
                  schema={paymentWarningsSchema}
                  values={{
                    supplier: {
                      paymentDetails: {
                        IBAN: supplier?.paymentDetails?.IBAN,
                      },
                    },
                    payments: payments,
                  }}
                >
                  <PaymentForm
                    invoiceId={invoice.invoiceId}
                    invoice={invoice}
                    currency={invoice.currency}
                    draftPayments={draftPayments}
                    recipientName={supplier?.supplierName ?? ""}
                    totalAmount={invoice.totalAmount}
                    iban={invoice.iban}
                    supplier={supplier}
                    onPay={handlePay}
                    onFieldFocus={handleFieldFocus}
                  />
                </ValidationRoot>
              )}
              <InvoiceCostCenterForm
                lineItems={lineItems}
                disabled={disabled}
              />
              <InvoiceBankReconciliation
                transactions={transactions}
                reconTriggered={invoice.reconTriggered}
              />
              <div className="flex flex-col">
                <TypographyLarge>{t("comments")}</TypographyLarge>
                <InvoiceComments invoiceId={invoice.invoiceId} />
              </div>
            </>
          )}
        </div>
        <div className="px-4 py-2">
          <ValidationContext.Consumer>
            {({ errors }) => (
              <NavigationBar
                disableConfirm={errors.length > 0}
                isDuplicate={invoice.isDuplicate}
                onDelete={handleDelete}
                onRemoveDuplicates={handleRemoveDuplicates}
                onConfirmClick={handleConfirm}
                confirmLabel={t("review")}
                showConfirmButton={
                  invoice.smbReviewStatus !== InvoiceSmbReviewStatus.REVIEWED
                }
              />
            )}
          </ValidationContext.Consumer>
        </div>
      </div>
    </ValidationRoot>
  );

  function useClearBlueBoxOnInvoiceChange() {
    useEffect(() => {
      updateBox();
    }, [invoice]);
  }
};

function CurrencyWarningBanner() {
  const { t } = useTranslation();

  return (
    <Alert variant="warning" className="mb-4 flex">
      <div className="flex flex-row gap-2 items-center">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="">
          {t("validation.payment.currency")}
        </AlertDescription>
      </div>
    </Alert>
  );
}
